// src/hooks/useSuperAdmin.ts - Hooks pour la gestion Super Admin

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  deleteField,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db, functions } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { Business, BusinessType } from '../types/business';
// ✅ UserRole supprimé - Les rôles sont maintenant des profils personnalisés
import { useAuth } from '../contexts/AuthContext';
import { handleFirestoreError } from '../utils/firestoreErrorHandler';
import { httpsCallable } from 'firebase/functions';
import { TaxConfiguration, TaxRate } from '../types/tax';
import { PaymentSplit } from '../types/entities';
import {
  allocatePaymentSplitsForLineItem,
  calculatePaymentSplitsTotal,
  mergePaymentSplitsByMethod,
  getPrimaryPaymentMethod,
} from '../utils/paymentUtils';

// Accès admin (Super Admin ou CSM) - utilisé par les hooks
const canAccessAdmin = (isSuperAdmin: boolean, isCSM: boolean) => isSuperAdmin || isCSM;

// Hook pour lister tous les business (Super Admin / CSM)
export const useAllBusinesses = () => {
  const { isSuperAdmin, isCSM } = useAuth();
  const hasAdminAccess = canAccessAdmin(isSuperAdmin, isCSM);

  return useQuery({
    queryKey: ['super_admin', 'businesses'],
    queryFn: async (): Promise<Business[]> => {
      if (!hasAdminAccess) {
        throw new Error('Accès non autorisé - Super Admin ou CSM requis');
      }

      try {
        const businessesQuery = query(
          collection(db, COLLECTIONS.businesses),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(businessesQuery).catch(error => {
          handleFirestoreError(error, 'useSuperAdmin - Businesses query');
          throw error;
        });
        
        return snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name: data.name,
            type: data.type,
            isActive: data.isActive,
            configurationInProgress: data.configurationInProgress ?? false,
            createdAt: data.createdAt?.toDate(),
            configuration: data.configuration,
            branding: data.branding,
            activeFeatures: data.activeFeatures,
            disabledFeatures: data.disabledFeatures,
          } as Business;
        });
      } catch (error) {
        console.error('Erreur lors du chargement des business:', error);
        throw error;
      }
    },
    enabled: hasAdminAccess,
  });
};

// Interface pour créer un nouveau business complet
export interface CreateBusinessData {
  // Données business
  businessName: string;
  businessType: BusinessType;
  
  // Configuration optionnelle
  configuration?: {
    loyalty?: {
      bonusPercentage: number;
      minimumUsage: number;
    };
    marketing?: {
      maxActiveCampaigns?: number;
    };
    inventory?: {
      enabled: boolean;
      lowStockThreshold: number;
    };
    tax?: {
      defaultRateId: string;
      selectedRates: string[];  // IDs des taux sélectionnés
      rates: TaxRate[];  // ✅ AJOUT : Objets TaxRate complets (prédéfinis + personnalisés)
      displayMode: 'inclusive' | 'exclusive';
      showTaxOnReceipts: boolean;
      companyTaxNumber?: string;
    };
  };

  // Branding obligatoire
  branding: {
    smsSenderName: string;
    phone: string;
    whatsappNumber: string;
    address: string;
    primaryColor?: string;
    secondaryColor?: string;
    description?: string;
  };

  // Premier admin (optionnel - peut être créé après via l'interface Super Admin)
  adminEmail?: string;
  adminName?: string;
  adminPassword?: string;
}

export interface SuperAdminUser {
  id: string;
  email: string;
  name: string;
  role?: string;  // ✅ NOUVEAU : Rôle métier (optionnel)
  roleProfileIds?: { [businessId: string]: string };  // Profils par business
  isSuperAdmin?: boolean;
  isCSM?: boolean;  // Customer Success Manager (accès admin avec restrictions)
  businessIds: string[];
  currentBusinessId?: string;
  isActive: boolean;
  createdAt?: Date | null;
  lastLogin?: Date | null;
  phone?: string | null;
}

const generateTaxRateId = (rateName: string, index: number): string => {
  if (rateName.includes('Cameroun')) return 'cameroon_standard';
  if (rateName.includes('Sénégal')) return 'senegal_standard';
  if (rateName.includes('Côte d\'Ivoire')) return 'ivory_coast_standard';
  if (rateName.includes('Burkina')) return 'burkina_standard';
  if (rateName.includes('Mali')) return 'mali_standard';
  if (rateName.includes('Exonéré')) return 'tax_exempt';
  return `rate_${index}`;
};

// Hook pour créer un business complet avec son premier admin
export const useCreateBusiness = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin } = useAuth();

  return useMutation({
    mutationFn: async (data: CreateBusinessData): Promise<{ businessId: string; adminUserId: string }> => {
      if (!isSuperAdmin) {
        throw new Error('Accès non autorisé - Super Admin requis');
      }

      const batch = writeBatch(db);

      try {
        // Préparer la configuration TVA avec des taux concrets
       // Préparer la configuration TVA avec des taux concrets
       let finalTaxConfiguration: TaxConfiguration | undefined;
        
       if (data.configuration?.tax) {
         const taxConfig = data.configuration.tax;
         
         // ✅ CORRECTION : Utiliser directement les objets TaxRate complets
         // Inclut les taux prédéfinis ET les taux personnalisés
         const selectedTaxRates = taxConfig.rates || [];

         finalTaxConfiguration = {
           defaultRateId: taxConfig.defaultRateId,
           rates: selectedTaxRates,
           displayMode: taxConfig.displayMode,
           showTaxOnReceipts: taxConfig.showTaxOnReceipts,
           companyTaxNumber: taxConfig.companyTaxNumber,
         };
       
        }

        // Appeler la Cloud Function
        const createBusinessFunction = httpsCallable(functions, 'createBusiness');
        console.log('=== SENDING TO CLOUD FUNCTION ===');
        console.log('data.configuration?.tax:', data.configuration?.tax);
        console.log('finalTaxConfiguration:', finalTaxConfiguration);
        const result = await createBusinessFunction({
          businessName: data.businessName,
          businessType: data.businessType,
          configuration: {
            ...data.configuration,
            tax: finalTaxConfiguration,
          },
          branding: data.branding,
          adminName: data.adminName,
          adminEmail: data.adminEmail,
          adminPassword: data.adminPassword,
        });

        const response = result.data as any;
        
        if (response.success) {
          return {
            businessId: response.businessId,
            adminUserId: response.adminUserId,
          };
        } else {
          throw new Error(response.message || 'Erreur lors de la création');
        }

      } catch (error: any) {
        console.error('Erreur Cloud Function:', error);
        
        // Gestion des erreurs de Cloud Function
        if (error.code === 'functions/already-exists') {
          throw new Error('Un compte existe déjà avec cette adresse email');
        }
        if (error.code === 'functions/permission-denied') {
          throw new Error('Permissions insuffisantes');
        }
        if (error.code === 'functions/invalid-argument') {
          throw new Error('Données invalides: ' + error.message);
        }
        
        throw new Error(error.message || 'Erreur lors de la création du business');
      }
    },
    onSuccess: (_result, variables) => {
      // Invalider la liste des business
      queryClient.invalidateQueries({ queryKey: ['super_admin', 'businesses'] });
      
      console.log(`✅ Business "${variables.businessName}" créé avec l'admin ${variables.adminEmail}`);
    },
    onError: (error) => {
      console.error('❌ Échec création business:', error);
    },
  });
};

export interface CreateSuperAdminUserInput {
  name: string;
  email: string;
  role?: string;  // ✅ NOUVEAU : Rôle métier (optionnel)
  roleProfileIds: { [businessId: string]: string };  // ✅ NOUVEAU : Profils par business
  businessIds: string[];
  currentBusinessId: string;
  password: string;
  phone?: string;
  isActive?: boolean;
  isSuperAdmin?: boolean;  // ✅ NOUVEAU : Flag SuperAdmin
  isCSM?: boolean;  // Customer Success Manager (exclusif avec isSuperAdmin)
}

export interface UpdateSuperAdminUserInput {
  userId: string;
  name?: string;
  email?: string;
  role?: string;  // ✅ NOUVEAU : Rôle métier (optionnel)
  roleProfileIds?: { [businessId: string]: string };  // ✅ NOUVEAU : Profils par business
  businessIds?: string[];
  currentBusinessId?: string;
  isActive?: boolean;
  password?: string;
  phone?: string;
  isSuperAdmin?: boolean;  // ✅ NOUVEAU : Flag SuperAdmin
  isCSM?: boolean;  // Customer Success Manager (exclusif avec isSuperAdmin)
}

// Hook pour lister tous les utilisateurs (Super Admin / CSM)
export const useAllUsers = () => {
  const { isSuperAdmin, isCSM } = useAuth();
  const hasAdminAccess = canAccessAdmin(isSuperAdmin, isCSM);

  return useQuery({
    queryKey: ['super_admin', 'users'],
    queryFn: async (): Promise<SuperAdminUser[]> => {
      if (!hasAdminAccess) {
        throw new Error('Accès non autorisé - Super Admin ou CSM requis');
      }

      try {
        const usersQuery = query(
          collection(db, COLLECTIONS.users),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(usersQuery).catch(error => {
          handleFirestoreError(error, 'useSuperAdmin - Users query');
          throw error;
        });

        return snapshot.docs.map(doc => {
          const data = doc.data() || {};
          // Migration depuis l'ancien format (role) vers le nouveau (roleProfileIds + isSuperAdmin)
          const isSuperAdminUser = data.role === 'super_admin' || data.isSuperAdmin === true;
          const isCSMUser = data.role === 'csm' || data.isCSM === true;
          
          // Récupérer roleProfileIds depuis Firestore
          const roleProfileIds = data.roleProfileIds;
          
          // Debug pour tous les utilisateurs pour voir ce qui est chargé
          console.log(`🔍 useAllUsers - User ${doc.id}:`, {
            'data.roleProfileIds': roleProfileIds,
            'type': typeof roleProfileIds,
            'isObject': roleProfileIds && typeof roleProfileIds === 'object',
            'keys': roleProfileIds && typeof roleProfileIds === 'object' ? Object.keys(roleProfileIds) : null,
          });
          
          const user: SuperAdminUser = {
            id: doc.id,
            email: data.email,
            name: data.name,
            role: data.role || undefined, // ✅ NOUVEAU : Rôle métier
            roleProfileIds: roleProfileIds || undefined,
            isSuperAdmin: isSuperAdminUser,
            isCSM: isCSMUser,
            businessIds: data.businessIds || (data.businessId ? [data.businessId] : []),
            currentBusinessId: data.currentBusinessId,
            isActive: data.isActive ?? true,
            createdAt: data.createdAt?.toDate?.() || null,
            lastLogin: data.lastLogin?.toDate?.() || null,
            phone: data.phone || null,
          };
          
          return user;
        });
      } catch (error) {
        console.error('Erreur lors du chargement des utilisateurs:', error);
        throw error;
      }
    },
    enabled: hasAdminAccess,
  });
};

export const useCreateSuperAdminUser = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin, isCSM } = useAuth();

  return useMutation({
    mutationFn: async (data: CreateSuperAdminUserInput) => {
      if (!canAccessAdmin(isSuperAdmin, isCSM)) {
        throw new Error('Accès non autorisé - Super Admin ou CSM requis');
      }

      const createUserFunction = httpsCallable(functions, 'createUserBySuperAdmin');
      const result = await createUserFunction(data);
      return result.data as { success: boolean; userId: string; employeeIds: { [businessId: string]: string } };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super_admin', 'users'] });
    },
  });
};

export const useUpdateSuperAdminUser = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin, isCSM } = useAuth();

  return useMutation({
    mutationFn: async (data: UpdateSuperAdminUserInput) => {
      if (!canAccessAdmin(isSuperAdmin, isCSM)) {
        throw new Error('Accès non autorisé - Super Admin ou CSM requis');
      }

      console.log('🔍 useUpdateSuperAdminUser - Données envoyées:', data);
      const updateUserFunction = httpsCallable(functions, 'updateUserBySuperAdmin');
      const result = await updateUserFunction(data);
      console.log('🔍 useUpdateSuperAdminUser - Résultat:', result.data);
      return result.data as { success: boolean; userId: string };
    },
    onSuccess: async () => {
      // Invalider et forcer le rechargement immédiat
      queryClient.invalidateQueries({ queryKey: ['super_admin', 'users'] });
      // Attendre un peu pour que Firestore soit à jour, puis refetch
      await queryClient.refetchQueries({ queryKey: ['super_admin', 'users'] });
      console.log('✅ Données utilisateurs rechargées après mise à jour');
    },
  });
};

// Hook pour activer/désactiver un utilisateur
export const useToggleUserStatus = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin, isCSM } = useAuth();

  return useMutation({
    mutationFn: async ({ userId, isActive }: { userId: string; isActive: boolean }) => {
      if (!canAccessAdmin(isSuperAdmin, isCSM)) {
        throw new Error('Accès non autorisé - Super Admin ou CSM requis');
      }
      // Un CSM ne peut pas activer/désactiver un utilisateur Super Admin
      if (isCSM) {
        const userSnap = await getDoc(doc(db, COLLECTIONS.users, userId));
        const targetData = userSnap.data();
        const targetIsSuperAdmin = targetData?.isSuperAdmin === true || targetData?.role === 'super_admin';
        if (targetIsSuperAdmin) {
          throw new Error('Un CSM ne peut pas activer ou désactiver un utilisateur Super Admin');
        }
      }

      const userRef = doc(db, COLLECTIONS.users, userId);
      await updateDoc(userRef, {
        isActive,
        updatedAt: Timestamp.now(),
      });

      return { userId, isActive };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super_admin', 'users'] });
    },
  });
};

// Hook pour désactiver/réactiver un business
export const useToggleBusinessStatus = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin, isCSM } = useAuth();

  return useMutation({
    mutationFn: async ({ businessId, isActive }: { businessId: string; isActive: boolean }) => {
      if (!canAccessAdmin(isSuperAdmin, isCSM)) {
        throw new Error('Accès non autorisé - Super Admin ou CSM requis');
      }

      const businessRef = doc(db, COLLECTIONS.businesses, businessId);
      await updateDoc(businessRef, {
        isActive,
        updatedAt: Timestamp.now(),
      });

      return { businessId, isActive };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['super_admin', 'businesses'] });
      
      const status = result.isActive ? 'activé' : 'désactivé';
      console.log(`Business ${status} avec succès`);
    },
  });
};

// Hook pour activer/désactiver le système de cagnotte
export const useToggleLoyaltySystem = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin } = useAuth();

  return useMutation({
    mutationFn: async ({ businessId, enabled }: { businessId: string; enabled: boolean }) => {
      if (!isSuperAdmin) {
        throw new Error('Accès non autorisé - Super Admin requis');
      }

      const businessRef = doc(db, COLLECTIONS.businesses, businessId);
      const businessSnap = await getDoc(businessRef);
      
      if (!businessSnap.exists()) {
        throw new Error('Business introuvable');
      }

      const currentData = businessSnap.data();
      const currentConfig = currentData.configuration || {};
      const currentLoyalty = currentConfig.loyalty || {};

      // Mettre à jour uniquement le flag enabled, en préservant les autres paramètres
      await updateDoc(businessRef, {
        'configuration.loyalty': {
          ...currentLoyalty,
          enabled,
        },
        updatedAt: Timestamp.now(),
      });

      return { businessId, enabled };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['super_admin', 'businesses'] });
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
      queryClient.invalidateQueries({ queryKey: ['businesses', result.businessId] });
      
      const status = result.enabled ? 'activé' : 'désactivé';
      console.log(`Système de cagnotte ${status} pour business ${result.businessId}`);
    },
  });
};

// Hook pour obtenir les statistiques globales (optionnel pour plus tard)
export const useSuperAdminStats = () => {
  const { isSuperAdmin } = useAuth();

  return useQuery({
    queryKey: ['super_admin', 'stats'],
    queryFn: async () => {
      if (!isSuperAdmin) {
        throw new Error('Accès non autorisé - Super Admin requis');
      }

      // Pour l'instant, statistiques simples
      const businessesQuery = query(collection(db, COLLECTIONS.businesses));
      const businessesSnap = await getDocs(businessesQuery).catch(error => {
        handleFirestoreError(error, 'useSuperAdmin - Businesses query');
        throw error;
      });
      
      const allBusinesses = businessesSnap.docs.map(doc => doc.data());
      const activeBusinesses = allBusinesses.filter(b => b.isActive);
      
      // Compter par type
      const businessesByType = allBusinesses.reduce((acc, business) => {
        acc[business.type] = (acc[business.type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      return {
        totalBusinesses: allBusinesses.length,
        activeBusinesses: activeBusinesses.length,
        inactiveBusinesses: allBusinesses.length - activeBusinesses.length,
        businessesByType,
      };
    },
    enabled: isSuperAdmin,
  });
};

// Hook pour migrer lastVisitDate des clients (remplir pour les clients existants)
export const useMigrateClientLastVisitDate = () => {
  const { isSuperAdmin } = useAuth();

  return useMutation({
    mutationFn: async (businessId: string) => {
      if (!isSuperAdmin) {
        throw new Error('Accès non autorisé - Super Admin requis');
      }

      console.log('🔄 useMigrateClientLastVisitDate - Business ID:', businessId);
      const migrateFunction = httpsCallable(functions, 'migrateClientLastVisitDate');
      const result = await migrateFunction({ businessId });
      console.log('🔄 useMigrateClientLastVisitDate - Résultat:', result.data);
      return result.data as {
        success: boolean;
        businessId: string;
        clients: { updated: number; skipped: number };
      };
    },
  });
};

// Hook pour migrer les prix de vente des produits à partir du prix d'achat (sellingPrice = costPrice)
export const useMigrateProductSellingPriceFromCost = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin } = useAuth();

  return useMutation({
    mutationFn: async (businessId: string) => {
      if (!isSuperAdmin) {
        throw new Error('Accès non autorisé - Super Admin requis');
      }

      const productsRef = collection(db, COLLECTIONS.products);
      const q = query(
        productsRef,
        where('businessId', '==', businessId)
      );
      const snapshot = await getDocs(q).catch(error => {
        handleFirestoreError(error, 'useMigrateProductSellingPriceFromCost');
        throw error;
      });

      const toUpdate: { id: string; costPrice: number }[] = [];
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        const costPrice = data.costPrice;
        if (costPrice != null && costPrice > 0) {
          toUpdate.push({ id: docSnap.id, costPrice: Number(costPrice) });
        }
      });

      const BATCH_SIZE = 500;
      let updated = 0;
      for (let i = 0; i < toUpdate.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        const chunk = toUpdate.slice(i, i + BATCH_SIZE);
        for (const p of chunk) {
          const productRef = doc(db, COLLECTIONS.products, p.id);
          batch.update(productRef, {
            sellingPrice: p.costPrice,
            updatedAt: Timestamp.now(),
          });
          updated++;
        }
        await batch.commit();
      }

      return { success: true, businessId, updated };
    },
    onSuccess: (_, businessId) => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', businessId] });
    },
  });
};

/**
 * Super Admin : initialise / complète configuration.products.packagingUnits
 * avec la liste standard (g, kg, … Plaquette, boîte(s), comprimé(s)).
 * Conserve les unités custom déjà présentes.
 */
export const useMigratePackagingUnitsDefaults = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin } = useAuth();

  return useMutation({
    mutationFn: async (businessId: string) => {
      if (!isSuperAdmin) {
        throw new Error('Accès non autorisé - Super Admin requis');
      }

      const { getPackagingUnits } = await import('../types/business');
      const {
        mergeMissingDefaultPackagingUnits,
        needsDefaultPackagingUnitsSync,
        DEFAULT_PACKAGING_UNITS,
      } = await import('../utils/productPackaging');

      const businessRef = doc(db, COLLECTIONS.businesses, businessId);
      const businessSnap = await getDoc(businessRef);
      if (!businessSnap.exists()) {
        throw new Error('Business introuvable');
      }

      const business = {
        id: businessSnap.id,
        ...(businessSnap.data() as object),
      } as Business;
      const before = getPackagingUnits(business);
      const after = mergeMissingDefaultPackagingUnits(before);
      const changed = needsDefaultPackagingUnitsSync(before);

      if (!changed) {
        return {
          success: true,
          businessId,
          changed: false,
          beforeCount: before.length,
          afterCount: after.length,
          units: after,
        };
      }

      await updateDoc(businessRef, {
        'configuration.products.packagingUnits': after,
        updatedAt: Timestamp.now(),
      });

      return {
        success: true,
        businessId,
        changed: true,
        beforeCount: before.length,
        afterCount: after.length,
        added: after.length - before.length,
        units: after,
        defaultsReference: [...DEFAULT_PACKAGING_UNITS],
      };
    },
    onSuccess: (_, businessId) => {
      queryClient.invalidateQueries({ queryKey: ['businesses', businessId] });
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
    },
  });
};

// Hook pour retirer le flag legacy recalculateCostOnProvisioning d'un business
export const useRemoveRecalculateCostOnProvisioning = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin, isCSM } = useAuth();
  const hasAdminAccess = canAccessAdmin(isSuperAdmin, isCSM);

  return useMutation({
    mutationFn: async (businessId: string) => {
      if (!hasAdminAccess) {
        throw new Error('Accès non autorisé - Super Admin ou CSM requis');
      }

      const businessRef = doc(db, COLLECTIONS.businesses, businessId);
      const businessSnap = await getDoc(businessRef);

      if (!businessSnap.exists()) {
        throw new Error('Business introuvable');
      }

      const businessData = businessSnap.data();
      const hasField = Object.prototype.hasOwnProperty.call(
        businessData?.configuration?.inventory || {},
        'recalculateCostOnProvisioning'
      );

      await updateDoc(businessRef, {
        'configuration.inventory.recalculateCostOnProvisioning': deleteField(),
        updatedAt: Timestamp.now(),
      });

      return { success: true, businessId, hadField: hasField };
    },
    onSuccess: (_, businessId) => {
      queryClient.invalidateQueries({ queryKey: ['super_admin', 'businesses'] });
      queryClient.invalidateQueries({ queryKey: ['businesses'] });
      queryClient.invalidateQueries({ queryKey: ['businesses', businessId] });
    },
  });
};

/**
 * Backfill / correction de stockConsumptionMode sur les services d’un business.
 *
 * Règles (alignées avec l’historique métier) :
 * - Produits consommés à la vente (`consumedProducts` non vide) ⇒ **direct** (prioritaire sur le template).
 * - Sinon, template OF (`manufacturingTemplate.defaultLines`) ⇒ **manufacturing_order**.
 * - Sinon ⇒ **none**.
 *
 * Met à jour :
 * - les services **sans** `stockConsumptionMode` (remplissage initial) ;
 * - les services qui ont des `consumedProducts` valides mais un mode **≠ direct** (ex. ancien défaut, erreur).
 */
export const useBackfillServiceStockConsumptionMode = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin } = useAuth();

  return useMutation({
    mutationFn: async (businessId: string) => {
      if (!isSuperAdmin) {
        throw new Error('Accès non autorisé - Super Admin requis');
      }

      const servicesRef = collection(db, COLLECTIONS.services);
      const snap = await getDocs(query(servicesRef, where('businessId', '==', businessId))).catch((error) => {
        handleFirestoreError(error, 'useBackfillServiceStockConsumptionMode');
        throw error;
      });

      const toUpdate: { id: string; mode: 'direct' | 'manufacturing_order' | 'none' }[] = [];
      let filledMissing = 0;
      let forcedDirect = 0;

      for (const d of snap.docs) {
        const data: any = d.data();
        const already = String(data.stockConsumptionMode ?? '').trim();

        const consumed = (Array.isArray(data.consumedProducts) ? data.consumedProducts : []).filter(
          (p: any) => p && String(p.productId ?? '').trim() && Number(p.quantity) > 0
        );
        const defaultLines = Array.isArray(data.manufacturingTemplate?.defaultLines)
          ? data.manufacturingTemplate.defaultLines
          : [];

        const desiredMode: 'direct' | 'manufacturing_order' | 'none' =
          consumed.length > 0
            ? 'direct'
            : defaultLines.length > 0
              ? 'manufacturing_order'
              : 'none';

        let newMode: 'direct' | 'manufacturing_order' | 'none' | null = null;
        if (!already) {
          newMode = desiredMode;
          filledMissing += 1;
        } else if (consumed.length > 0 && already !== 'direct') {
          newMode = 'direct';
          forcedDirect += 1;
        }

        if (newMode !== null) {
          toUpdate.push({ id: d.id, mode: newMode });
        }
      }

      const BATCH_SIZE = 450;
      let updated = 0;
      for (let i = 0; i < toUpdate.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        const chunk = toUpdate.slice(i, i + BATCH_SIZE);
        for (const s of chunk) {
          batch.update(doc(db, COLLECTIONS.services, s.id), {
            stockConsumptionMode: s.mode,
          });
          updated += 1;
        }
        await batch.commit();
      }

      return { success: true, businessId, scanned: snap.size, updated, filledMissing, forcedDirect };
    },
    onSuccess: (_data, businessId) => {
      queryClient.invalidateQueries({ queryKey: ['business', 'services', businessId] });
    },
  });
};

const PAYMENT_SPLIT_MIGRATION_TOL = 0.01;

function normalizedCartSplitsKey(splits: PaymentSplit[] | undefined): string {
  const merged = mergePaymentSplitsByMethod(splits ?? []);
  return JSON.stringify(
    [...merged]
      .sort((a, b) => a.method.localeCompare(b.method))
      .map((s) => ({ method: s.method, amount: Math.round(s.amount * 100) / 100 }))
  );
}

/**
 * Corrige les `paymentSplits` des ventes groupées (`transactionGroupId`) où chaque ligne
 * dupliquait le panier entier : répartition par ligne via `allocatePaymentSplitsForLineItem`.
 */
export const useMigrateGroupedTransactionPaymentSplits = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin } = useAuth();

  return useMutation({
    mutationFn: async (businessId: string) => {
      if (!isSuperAdmin) {
        throw new Error('Accès non autorisé - Super Admin requis');
      }

      type GroupLine = {
        collectionName: typeof COLLECTIONS.prestations | typeof COLLECTIONS.productSales;
        docId: string;
        transactionIndex: number;
        finalPrice: number;
        paymentSplits?: PaymentSplit[];
      };

      const prestSnap = await getDocs(
        query(collection(db, COLLECTIONS.prestations), where('businessId', '==', businessId))
      ).catch((error) => {
        handleFirestoreError(error, 'useMigrateGroupedTransactionPaymentSplits - prestations');
        throw error;
      });

      const salesSnap = await getDocs(
        query(collection(db, COLLECTIONS.productSales), where('businessId', '==', businessId))
      ).catch((error) => {
        handleFirestoreError(error, 'useMigrateGroupedTransactionPaymentSplits - productSales');
        throw error;
      });

      const byGroup = new Map<string, GroupLine[]>();

      for (const d of prestSnap.docs) {
        const data = d.data() as Record<string, unknown>;
        const gid = typeof data.transactionGroupId === 'string' ? data.transactionGroupId.trim() : '';
        if (!gid) continue;
        const ps = data.paymentSplits as PaymentSplit[] | undefined;
        const line: GroupLine = {
          collectionName: COLLECTIONS.prestations,
          docId: d.id,
          transactionIndex: Number(data.transactionIndex ?? 0) || 0,
          finalPrice: Number(data.finalPrice ?? data.price ?? 0) || 0,
          paymentSplits: Array.isArray(ps) && ps.length ? ps : undefined,
        };
        const arr = byGroup.get(gid) ?? [];
        arr.push(line);
        byGroup.set(gid, arr);
      }

      for (const d of salesSnap.docs) {
        const data = d.data() as Record<string, unknown>;
        const gid = typeof data.transactionGroupId === 'string' ? data.transactionGroupId.trim() : '';
        if (!gid) continue;
        const ps = data.paymentSplits as PaymentSplit[] | undefined;
        const line: GroupLine = {
          collectionName: COLLECTIONS.productSales,
          docId: d.id,
          transactionIndex: Number(data.transactionIndex ?? 0) || 0,
          finalPrice: Number(data.finalPrice ?? data.totalPrice ?? 0) || 0,
          paymentSplits: Array.isArray(ps) && ps.length ? ps : undefined,
        };
        const arr = byGroup.get(gid) ?? [];
        arr.push(line);
        byGroup.set(gid, arr);
      }

      type Pending = {
        collectionName: typeof COLLECTIONS.prestations | typeof COLLECTIONS.productSales;
        docId: string;
        paymentSplits: PaymentSplit[];
        paymentMethod: string;
      };
      const pending: Pending[] = [];

      let groupsScanned = 0;
      let groupsSkipped = 0;
      let groupsFixed = 0;

      const groupedEntries: [string, GroupLine[]][] = Array.from(byGroup.entries());
      for (const [, lines] of groupedEntries) {
        groupsScanned++;
        if (lines.length < 2) {
          groupsSkipped++;
          continue;
        }

        const withSplits = lines.filter((l: GroupLine) => l.paymentSplits && l.paymentSplits.length);
        if (withSplits.length < 2) {
          groupsSkipped++;
          continue;
        }

        const key0 = normalizedCartSplitsKey(withSplits[0].paymentSplits);
        if (!withSplits.every((l: GroupLine) => normalizedCartSplitsKey(l.paymentSplits) === key0)) {
          groupsSkipped++;
          continue;
        }

        const transactionTotal = lines.reduce((s: number, l: GroupLine) => s + l.finalPrice, 0);
        if (!(transactionTotal > PAYMENT_SPLIT_MIGRATION_TOL)) {
          groupsSkipped++;
          continue;
        }

        const mergedAll = mergePaymentSplitsByMethod(
          withSplits.flatMap((l: GroupLine) => l.paymentSplits as PaymentSplit[])
        );
        const mergedTotal = calculatePaymentSplitsTotal(mergedAll);
        if (!(mergedTotal > transactionTotal + PAYMENT_SPLIT_MIGRATION_TOL)) {
          groupsSkipped++;
          continue;
        }

        const sortedByIndex = [...withSplits].sort(
          (a: GroupLine, b: GroupLine) => a.transactionIndex - b.transactionIndex
        );
        const cartSplits = mergePaymentSplitsByMethod(sortedByIndex[0].paymentSplits as PaymentSplit[]);
        if (!cartSplits.length) {
          groupsSkipped++;
          continue;
        }

        let wroteInGroup = 0;
        for (const line of lines) {
          if (!line.paymentSplits?.length) continue;
          const newSplits = allocatePaymentSplitsForLineItem(cartSplits, line.finalPrice, transactionTotal);
          if (!newSplits?.length) continue;
          const pm = getPrimaryPaymentMethod(newSplits) ?? 'espece';
          pending.push({
            collectionName: line.collectionName,
            docId: line.docId,
            paymentSplits: newSplits,
            paymentMethod: pm,
          });
          wroteInGroup++;
        }
        if (wroteInGroup > 0) groupsFixed++;
        else groupsSkipped++;
      }

      const BATCH_SIZE = 450;
      let prestationsUpdated = 0;
      let productSalesUpdated = 0;

      for (let i = 0; i < pending.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        const chunk = pending.slice(i, i + BATCH_SIZE);
        for (const u of chunk) {
          batch.update(doc(db, u.collectionName, u.docId), {
            paymentSplits: u.paymentSplits,
            paymentMethod: u.paymentMethod,
            updatedAt: Timestamp.now(),
          });
          if (u.collectionName === COLLECTIONS.prestations) prestationsUpdated += 1;
          else productSalesUpdated += 1;
        }
        await batch.commit();
      }

      return {
        success: true,
        businessId,
        groupsScanned,
        groupsFixed,
        groupsSkipped,
        prestationsUpdated,
        productSalesUpdated,
        linesUpdated: prestationsUpdated + productSalesUpdated,
      };
    },
    onSuccess: (_data, businessId) => {
      queryClient.invalidateQueries({ queryKey: ['prestations', businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'productSales', businessId] });
    },
  });
};

/** Migration multi-sites : affecte le siteId du point de vente par défaut aux données existantes. */
export const useMigrateMultiSitesData = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin } = useAuth();

  return useMutation({
    mutationFn: async (params: { businessId: string; pointOfSaleSiteId: string; pointOfSaleSiteName: string }) => {
      if (!isSuperAdmin) throw new Error('Accès non autorisé - Super Admin requis');
      const { businessId, pointOfSaleSiteId, pointOfSaleSiteName } = params;

      const productsRef = collection(db, COLLECTIONS.products);
      const productsSnap = await getDocs(query(productsRef, where('businessId', '==', businessId))).catch((e) => {
        handleFirestoreError(e, 'useMigrateMultiSitesData - products');
        throw e;
      });

      const BATCH_SIZE = 400;
      let productsUpdated = 0;
      const productDocs = productsSnap.docs;
      for (let i = 0; i < productDocs.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        const chunk = productDocs.slice(i, i + BATCH_SIZE);
        for (const d of chunk) {
          const data = d.data();
          const currentStock = Number(data.currentStock ?? 0);
          const existingBySite = (data.stockBySite as Record<string, number>) || {};
          const updatedBySite = { ...existingBySite, [pointOfSaleSiteId]: currentStock };
          batch.update(doc(db, COLLECTIONS.products, d.id), {
            stockBySite: updatedBySite,
            updatedAt: Timestamp.now(),
          });
          productsUpdated++;
        }
        if (chunk.length > 0) await batch.commit();
      }

      const salesRef = collection(db, COLLECTIONS.productSales);
      const salesSnap = await getDocs(
        query(salesRef, where('businessId', '==', businessId), orderBy('date', 'desc'))
      ).catch((e) => {
        handleFirestoreError(e, 'useMigrateMultiSitesData - productSales');
        throw e;
      });

      let salesUpdated = 0;
      const salesDocs = salesSnap.docs.filter((d) => !d.data().siteId);
      for (let i = 0; i < salesDocs.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        const chunk = salesDocs.slice(i, i + BATCH_SIZE);
        for (const d of chunk) {
          batch.update(doc(db, COLLECTIONS.productSales, d.id), {
            siteId: pointOfSaleSiteId,
            siteName: pointOfSaleSiteName,
            updatedAt: Timestamp.now(),
          });
          salesUpdated++;
        }
        if (chunk.length > 0) await batch.commit();
      }

      // Mouvements de stock : rattacher les anciens mouvements sans site
      const movementsRef = collection(db, COLLECTIONS.stockMovements);
      const movementsSnap = await getDocs(
        query(movementsRef, where('businessId', '==', businessId))
      ).catch((e) => {
        handleFirestoreError(e, 'useMigrateMultiSitesData - stockMovements');
        throw e;
      });

      let movementsUpdated = 0;
      const movementDocs = movementsSnap.docs.filter((d) => !d.data().siteId);
      for (let i = 0; i < movementDocs.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        const chunk = movementDocs.slice(i, i + BATCH_SIZE);
        for (const d of chunk) {
          batch.update(doc(db, COLLECTIONS.stockMovements, d.id), {
            siteId: pointOfSaleSiteId,
            siteName: pointOfSaleSiteName,
            updatedAt: Timestamp.now(),
          });
          movementsUpdated++;
        }
        if (chunk.length > 0) await batch.commit();
      }

      // Approvisionnements : rattacher les anciens deliveries sans site
      const deliveriesRef = collection(db, COLLECTIONS.deliveries);
      const deliveriesSnap = await getDocs(
        query(deliveriesRef, where('businessId', '==', businessId))
      ).catch((e) => {
        handleFirestoreError(e, 'useMigrateMultiSitesData - deliveries');
        throw e;
      });

      let deliveriesUpdated = 0;
      const deliveryDocs = deliveriesSnap.docs.filter((d) => !d.data().siteId);
      for (let i = 0; i < deliveryDocs.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        const chunk = deliveryDocs.slice(i, i + BATCH_SIZE);
        for (const d of chunk) {
          batch.update(doc(db, COLLECTIONS.deliveries, d.id), {
            siteId: pointOfSaleSiteId,
            siteName: pointOfSaleSiteName,
            updatedAt: Timestamp.now(),
          });
          deliveriesUpdated++;
        }
        if (chunk.length > 0) await batch.commit();
      }

      return {
        success: true,
        businessId,
        productsUpdated,
        salesUpdated,
        movementsUpdated,
        deliveriesUpdated,
      };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', variables.businessId] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'productSales', variables.businessId] });
    },
  });
};

/** Migration dédiée : initialise minStockAlertBySite pour le point de vente par défaut. */
export const useMigrateProductAlertThresholdsBySite = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin } = useAuth();

  return useMutation({
    mutationFn: async (params: { businessId: string; pointOfSaleSiteId: string }) => {
      if (!isSuperAdmin) throw new Error('Accès non autorisé - Super Admin requis');
      const { businessId, pointOfSaleSiteId } = params;

      const productsRef = collection(db, COLLECTIONS.products);
      const productsSnap = await getDocs(query(productsRef, where('businessId', '==', businessId))).catch((e) => {
        handleFirestoreError(e, 'useMigrateProductAlertThresholdsBySite - products');
        throw e;
      });

      const BATCH_SIZE = 400;
      let updated = 0;
      const productDocs = productsSnap.docs.filter((d) => {
        const bySite = (d.data().minStockAlertBySite as Record<string, number>) || {};
        return bySite[pointOfSaleSiteId] === undefined;
      });

      for (let i = 0; i < productDocs.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        const chunk = productDocs.slice(i, i + BATCH_SIZE);
        for (const d of chunk) {
          const data = d.data();
          const existingThresholdBySite = (data.minStockAlertBySite as Record<string, number>) || {};
          const globalThreshold = Number(data.minStockAlert ?? 0);
          const thresholdForPos = Number.isFinite(globalThreshold) ? Math.max(0, globalThreshold) : 0;

          batch.update(doc(db, COLLECTIONS.products, d.id), {
            minStockAlertBySite: {
              ...existingThresholdBySite,
              [pointOfSaleSiteId]: thresholdForPos,
            },
            updatedAt: Timestamp.now(),
          });
          updated++;
        }
        if (chunk.length > 0) await batch.commit();
      }

      return {
        success: true,
        businessId,
        updated,
        scanned: productsSnap.docs.length,
      };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products', variables.businessId] });
    },
  });
};

/** Rattrapage : complète unitCost/totalCost des anciens mouvements "Vente produit". */
export const useBackfillSaleMovementCosts = () => {
  const queryClient = useQueryClient();
  const { isSuperAdmin, isCSM } = useAuth();
  const hasAdminAccess = canAccessAdmin(isSuperAdmin, isCSM);

  return useMutation({
    mutationFn: async (params: { businessId: string }) => {
      if (!hasAdminAccess) throw new Error('Accès non autorisé - Super Admin ou CSM requis');
      const { businessId } = params;

      const movementsRef = collection(db, COLLECTIONS.stockMovements);
      const movementsSnap = await getDocs(
        query(
          movementsRef,
          where('businessId', '==', businessId),
          where('reason', '==', 'Vente produit')
        )
      ).catch((e) => {
        handleFirestoreError(e, 'useBackfillSaleMovementCosts - stockMovements');
        throw e;
      });

      const toNonNegativeNumber = (value: unknown, fallback = 0) => {
        const n = Number(value);
        if (!Number.isFinite(n)) return fallback;
        return Math.max(0, n);
      };

      const productCostCache = new Map<string, number>();
      const salePriceCache = new Map<string, { unitPrice: number; totalPrice: number }>();
      const BATCH_SIZE = 400;
      let scanned = 0;
      let updated = 0;
      let skipped = 0;
      let missingProducts = 0;

      const candidates = movementsSnap.docs.filter((d) => {
        const data = d.data();
        const hasUnitCost = Number.isFinite(Number(data.unitCost));
        const hasTotalCost = Number.isFinite(Number(data.totalCost));
        return !(hasUnitCost && hasTotalCost);
      });

      for (let i = 0; i < candidates.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        const chunk = candidates.slice(i, i + BATCH_SIZE);

        for (const movementDoc of chunk) {
          scanned++;
          const movement = movementDoc.data();
          const hasUnitCost = Number.isFinite(Number(movement.unitCost));
          const hasTotalCost = Number.isFinite(Number(movement.totalCost));
          const quantity = toNonNegativeNumber(movement.quantity, 0);
          const productId = movement.productId as string | undefined;

          if (!productId) {
            skipped++;
            continue;
          }

          let unitCostFromProduct = productCostCache.get(productId);
          let unitPriceFromSale: number | undefined;
          let totalPriceFromSale: number | undefined;

          const reference = String(movement.reference || '');
          if (reference.startsWith('VENTE-')) {
            const saleId = reference.slice('VENTE-'.length).trim();
            if (saleId) {
              let cachedSale = salePriceCache.get(saleId);
              if (!cachedSale) {
                const saleSnap = await getDoc(doc(db, COLLECTIONS.productSales, saleId));
                if (saleSnap.exists()) {
                  const saleData = saleSnap.data();
                  if (saleData.businessId === businessId) {
                    const saleUnitPrice = toNonNegativeNumber(saleData.unitPrice, 0);
                    const saleTotalPrice = toNonNegativeNumber(
                      saleData.finalPrice ?? saleData.totalPrice ?? saleUnitPrice * quantity,
                      saleUnitPrice * quantity
                    );
                    cachedSale = { unitPrice: saleUnitPrice, totalPrice: saleTotalPrice };
                    salePriceCache.set(saleId, cachedSale);
                  }
                }
              }

              if (cachedSale) {
                unitPriceFromSale = cachedSale.unitPrice;
                totalPriceFromSale = cachedSale.totalPrice;
              }
            }
          }

          if (unitCostFromProduct == null) {
            const productSnap = await getDoc(doc(db, COLLECTIONS.products, productId));
            if (!productSnap.exists()) {
              missingProducts++;
              skipped++;
              continue;
            }
            const productData = productSnap.data();
            if (productData.businessId !== businessId) {
              skipped++;
              continue;
            }
            unitCostFromProduct = toNonNegativeNumber(productData.averageCost ?? productData.costPrice, 0);
            productCostCache.set(productId, unitCostFromProduct);
          }

          const fallbackUnit = unitPriceFromSale ?? unitCostFromProduct;
          const fallbackTotal = totalPriceFromSale ?? fallbackUnit * quantity;

          const nextUnitCost = hasUnitCost
            ? toNonNegativeNumber(movement.unitCost, fallbackUnit)
            : fallbackUnit;
          const nextTotalCost = hasTotalCost
            ? toNonNegativeNumber(movement.totalCost, fallbackTotal)
            : fallbackTotal;

          const updateData: Record<string, unknown> = {};
          if (!hasUnitCost) updateData.unitCost = nextUnitCost;
          if (!hasTotalCost) updateData.totalCost = nextTotalCost;

          if (Object.keys(updateData).length === 0) {
            skipped++;
            continue;
          }

          batch.update(doc(db, COLLECTIONS.stockMovements, movementDoc.id), {
            ...updateData,
            updatedAt: Timestamp.now(),
          });
          updated++;
        }

        if (chunk.length > 0) await batch.commit();
      }

      return {
        success: true,
        businessId,
        scanned,
        updated,
        skipped,
        missingProducts,
        candidates: candidates.length,
      };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'stockMovements', variables.businessId] });
    },
  });
};

// Hook pour créer les profils système pour un business existant
// ✅ SUPPRIMÉ : Hook useCreateSystemRoleProfiles
// Les Super Admins créent et configurent les profils de rôle manuellement via l'interface